import { useEffect, useMemo, useRef } from 'react';
import { useIsomorphicLayoutEffect as useLayoutEffect } from '../isomorphic-layout-effect';
import { createFocusTrap } from './FocusTrap';
import { useRestoreFocus } from './useRestoreFocus';
/**
 * Creates focus trap for selected dom-node.
 *
 * @example
 * const Modal = (props) => {
 *   const { isVisible, children } = props
 *   const scopeRef = useRef(null)
 *
 *   useFocusTrap({ enabled: isVisible, scopeRef })
 *
 *   return (
 *     <div ref={scopeRef}>{children}</div>
 *   )
 * }
 */
export function useFocusTrap(props) {
    var scopeRef = props.scopeRef, enabled = props.enabled, restoreFocusRef = props.restoreFocusRef, _a = props.restoreFocus, restoreFocus = _a === void 0 ? true : _a, _b = props.autoFocus, autoFocus = _b === void 0 ? true : _b;
    var instance = useRef(null);
    var ref = useRef(null);
    var trapOptions = useMemo(function () {
        return {
            autoFocus: autoFocus,
        };
    }, [autoFocus]);
    useEffect(function () {
        if (instance.current) {
            instance.current.setOptions(trapOptions);
        }
    }, [trapOptions]);
    useLayoutEffect(function () {
        if (ref.current === scopeRef.current) {
            return;
        }
        ref.current = scopeRef.current;
        if (instance.current) {
            instance.current.deactivate();
            instance.current = null;
        }
        if (ref.current) {
            instance.current = createFocusTrap(ref.current, trapOptions);
            if (enabled) {
                instance.current.activate();
            }
        }
    });
    useLayoutEffect(function () {
        if (!enabled || !instance.current) {
            return;
        }
        instance.current.activate();
        return function () {
            var _a;
            (_a = instance.current) === null || _a === void 0 ? void 0 : _a.deactivate();
        };
    }, [enabled]);
    useRestoreFocus({ enabled: enabled && restoreFocus, restoreFocusRef: restoreFocusRef });
}
